import type { VariableValueType } from "../variable-picker/types";

/**
 * Settings key a token is written to when a variable binds onto an element.
 * Media/video/iframe/icon share `dynamicVariableSrc`; a `data` variable owns
 * a whole table via `dataDynamicVariable`. Typed rather than a bare `string`
 * so a typo'd settings key is a compile error, not a silently-dead write that
 * only surfaces at publish time.
 */
export type VariableBindField =
  | "dynamicVariableSrc"
  | "dataDynamicVariable"
  | "dynamicVaribleNumber";

/**
 * Where a variable of a given type may land.
 *
 * `bind` — element names it can attach to (settings-level binding, e.g.
 * `dynamicVariableSrc` / `dataDynamicVariable` / an inline token).
 * `create` — element created when dropped into a gap; `null` means the type
 * has no mechanism to bind OR create and is therefore blocked at every drop
 * target (see `color`/`list` below) — this is a drop-time restriction, not a
 * statement about whether the row itself is draggable (variable-row.tsx is
 * unconditionally draggable; gating the drag handle is a separate UX call).
 */
export interface VariableScope {
  bind: readonly string[];
  create: string | null;
  /**
   * Settings key the token is written to when binding. `null` for types that
   * never bind to an element (text-ish types land as an inline token in the
   * content instead; NONE-scope types don't land anywhere).
   */
  bindField: VariableBindField | null;
}

/** Types that resolve to inline text; also the fallback for unknown types. */
const TEXTISH: VariableScope = {
  bind: ["RichText", "Table"],
  create: "RichText",
  // Text lands as an inline token in the content, not in a settings field.
  bindField: null,
};

/** No mechanism exists to bind or create these — blocked at every target. */
const NONE: VariableScope = { bind: [], create: null, bindField: null };

// Record<VariableValueType, …> (not Record<string, …>) so a future member
// added to VariableValueType and forgotten here is a compile error, not a
// silent fall-through indistinguishable from a typo'd/unknown string.
const SCOPES: Record<VariableValueType, VariableScope> = {
  image: {
    bind: ["Image", "Logo"],
    create: "Image",
    bindField: "dynamicVariableSrc",
  },
  // "media" is the legacy combined image/video type (kept for already-stored
  // variables — see the comment on VariableValueType in
  // variable-picker/types.ts), so unlike "image" it can also carry a video
  // URL and bind onto a Video element.
  media: {
    bind: ["Image", "Logo", "Video"],
    create: "Image",
    bindField: "dynamicVariableSrc",
  },
  // Video does NOT bind Logo: a Logo resolves dynamicVariableSrc onto an
  // <img src>, so a video URL there renders as a broken image.
  video: { bind: ["Video"], create: "Video", bindField: "dynamicVariableSrc" },
  iframe: {
    bind: ["Embedded"],
    create: "Embedded",
    bindField: "dynamicVariableSrc",
  },
  // A data variable's matrix fills a whole Table (cells, rows, columns) or a
  // whole Chart grid (row 0 headers, column 0 titles) — see the two
  // parse*DataDynamicVar utils. Creating from scratch still builds a Table.
  data: {
    bind: ["Table", "Chart"],
    create: "Table",
    bindField: "dataDynamicVariable",
  },
  // No `icon`-typed variable can exist today (IFsVariableTypeEnum has no icon
  // member; nothing emits a PickerVariable with type "icon"), so this scope is
  // groundwork ahead of the type shipping, not something reachable yet. Kept
  // per product's explicit ask. Still missing before an icon variable can
  // actually ship: the `variable=` badge slot in Icon.editor.tsx, a
  // `dynamicVariableSrc` settings row for Icon (image.ts/logo.ts/video.ts all
  // have one), an `icon` entry in CONTROL_BY_TYPE (control-registry.ts), and
  // static-library runtime resolution of dynamicVariableSrc for Icon.
  icon: { bind: ["Icon"], create: "Icon", bindField: "dynamicVariableSrc" },
  input: TEXTISH,
  number: TEXTISH,
  date: TEXTISH,
  select: TEXTISH,
  multiselect: TEXTISH,
  ai: TEXTISH,
  computed: TEXTISH,
  system: TEXTISH,
  hubspot: TEXTISH,
  fathom: TEXTISH,
  calculation: TEXTISH,
  // Color binds through the colour popover, never a drop.
  color: NONE,
  // Superseded by repeatable content; hidden from authoring surfaces.
  list: NONE,
};

/** Unknown types degrade to inline text rather than becoming undraggable. */
export const getVariableScope = (
  type: VariableValueType | string,
): VariableScope => SCOPES[type as VariableValueType] ?? TEXTISH;

/**
 * Types whose value can be a number: a counter (Animated numbers) shows it, a
 * chart data cell plots it. A sender-typed `input`, a CRM / Fathom field and a
 * formula routinely carry one.
 */
const NUMBER_VALUE_TYPES: ReadonlySet<VariableValueType> =
  new Set<VariableValueType>([
    "number",
    "input",
    "calculation",
    "hubspot",
    "fathom",
  ]);

export const holdsNumber = (type: VariableValueType | string): boolean =>
  NUMBER_VALUE_TYPES.has(type as VariableValueType);

/** The Animated numbers element: binds a number-holding variable as its value
 * (`dynamicVaribleNumber`), on top of each type's own scope. */
const COUNTER_ELEMENT = "AnimatedNumbers";

const bindsCounter = (
  type: VariableValueType | string,
  elementName: string | undefined | null,
): boolean => elementName === COUNTER_ELEMENT && holdsNumber(type);

export const canBindTo = (
  type: VariableValueType | string,
  elementName: string | undefined | null,
): boolean =>
  !!elementName &&
  (getVariableScope(type).bind.includes(elementName) ||
    bindsCounter(type, elementName));

/**
 * The settings key a variable of this type binds through on THIS element, or
 * null when it lands inline as text instead. Per element, not per type: a
 * number variable binds a counter (`dynamicVaribleNumber`) but is inline text
 * in a RichText.
 */
export const bindFieldFor = (
  type: VariableValueType | string,
  elementName: string | undefined | null,
): VariableBindField | null =>
  bindsCounter(type, elementName)
    ? "dynamicVaribleNumber"
    : getVariableScope(type).bindField;

export const canCreateElement = (type: VariableValueType | string): boolean =>
  getVariableScope(type).create !== null;

/**
 * Whether this type has no mechanism to land anywhere at all — can't create
 * an element and can't bind to one (the NONE scope: `color`, `list`). Named
 * so the notion lives in one place rather than being re-derived inline at
 * each call site.
 */
export const isInsertableType = (type: VariableValueType | string): boolean =>
  canCreateElement(type) || getVariableScope(type).bind.length > 0;

/**
 * Whether a variable may land as an inline token inside existing text. Only
 * the text-ish types bind to RichText; media/data/icon/iframe must bind to
 * their own element instead, so they get no caret and no inline insert.
 */
export const canLandInlineInText = (
  type: VariableValueType | string,
): boolean => canBindTo(type, "RichText");

/**
 * Value types the Σ formula editor accepts as an operand: `input`, `number`,
 * `ai` and `select` — the product's list, for both the `{{` typeahead and
 * "Variables used in this story". Everything else (media, colour, data, date,
 * system, CRM/Fathom fields) is filtered out.
 *
 * `calculation` is deliberately absent: the runtime evaluates every formula
 * against the raw data, so one formula can never feed another.
 *
 * Deliberately NOT derived from `canLandInlineInText`: "renders as text" and
 * "can be an operand" are different questions, and tying them together would
 * silently widen the operand set the next time a text-ish type is added.
 */
const FORMULA_OPERANDS: ReadonlySet<VariableValueType> =
  new Set<VariableValueType>(["input", "number", "ai", "select"]);

/**
 * Whether a variable of this type may be used as an operand in a calculation
 * formula. Kept here beside {@link canLandInlineInText} so every "where can
 * this variable type land" rule lives in one file.
 *
 * An unknown/un-catalogued type is NOT an operand on its own — callers that
 * synthesize a fallback entry for a used-but-uncatalogued token type it
 * `"input"` (which is allowed) rather than leaving it unknown here.
 */
export const canBeFormulaOperand = (
  type: VariableValueType | string,
): boolean => FORMULA_OPERANDS.has(type as VariableValueType);

/**
 * Whether a drop may land at all. A `replace` (onto an element) requires a
 * bindable target; a gap position requires only that the type can create.
 *
 * The add-element placeholder also reports `position: "replace"`
 * (find-closest-item.ts), but it is an empty container/slide, not a real
 * element — it carries no `data-element-name`, so `canBindTo` would always
 * fail it. A placeholder drop is always a CREATE, so `isPlaceholder` short-
 * circuits straight to `canCreateElement` before the bind check.
 */
export const isDropAllowed = ({
  type,
  position,
  targetElementName,
  isPlaceholder,
}: {
  type: VariableValueType | string;
  position: "above" | "below" | "replace";
  targetElementName?: string | null;
  isPlaceholder?: boolean;
}): boolean => {
  if (isPlaceholder) return canCreateElement(type);
  return position === "replace"
    ? canBindTo(type, targetElementName)
    : canCreateElement(type);
};
